import { Alert, Box, Button, Card, Checkbox, Stack, Text, Tooltip } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { IconStar } from '@/components/icons'
import { formatDateTimeDot } from '@/lib/ticket-display'
import type {
  TicketProductReviewStatusResponse,
  TicketReviewStatusResponse,
} from '@/types/api'

type TicketReviewsCardProps = {
  showMyWorkReview: boolean
  showProductReview: boolean
  isEmailChannel: boolean
  reviewStatus: TicketReviewStatusResponse | null
  productReviewStatus: TicketProductReviewStatusResponse | null
  hasOtherProductRequests: boolean
  canPostProductReview: boolean
  reviewBusy: boolean
  productReviewBusy: boolean
  onRequestReview: () => void
  onRequestProductReview: () => void
  onToggleProductReviewReceived: (received: boolean) => void
}

export function TicketReviewsCard({
  showMyWorkReview,
  showProductReview,
  isEmailChannel,
  reviewStatus,
  productReviewStatus,
  hasOtherProductRequests,
  canPostProductReview,
  reviewBusy,
  productReviewBusy,
  onRequestReview,
  onRequestProductReview,
  onToggleProductReviewReceived,
}: TicketReviewsCardProps) {
  if (!showMyWorkReview && !showProductReview) return null

  const confirmRequestReview = () => {
    openConfirmModal({
      title: 'Запросить оценку?',
      children: (
        <Text size="sm" c="dimmed">
          Клиенту будет отправлен запрос оценить вашу работу по этому обращению.
        </Text>
      ),
      labels: { confirm: 'Отправить', cancel: 'Отмена' },
      onConfirm: onRequestReview,
    })
  }

  const confirmRequestProductReview = () => {
    openConfirmModal({
      title: 'Запросить отзыв о продукте?',
      children: (
        <Text size="sm" c="dimmed">
          Будет отправлен запрос отзыва о продукте (ссылки из настроек проекта).
        </Text>
      ),
      labels: { confirm: 'Запросить', cancel: 'Отмена' },
      onConfirm: onRequestProductReview,
    })
  }

  return (
    <Card withBorder padding="sm" radius="md">
      <Text fw={600} size="xs" mb={8}>
        Оценки и отзывы
      </Text>
      <Stack gap="xs">
        {isEmailChannel ? (
          <Alert color="gray" variant="light" p="xs">
            <Text size="xs">Для канала email запрос оценки работы пока не поддерживается.</Text>
          </Alert>
        ) : null}
        {showMyWorkReview ? (
          reviewStatus?.requested ? (
            <Text size="xs" c="dimmed">
              Запрос оценки уже отправлен
              {reviewStatus.submitted_at ? ' · ответ получен' : ''}
              {reviewStatus.rating != null ? ` · оценка: ${reviewStatus.rating}` : ''}
            </Text>
          ) : (
            <Tooltip
              label="Для канала email запрос оценки пока не поддерживается"
              disabled={!isEmailChannel}
              position="top"
              withArrow
              w={260}
            >
              <Box style={{ width: '100%' }}>
                <Button
                  size="compact-xs"
                  variant="light"
                  fullWidth
                  leftSection={<IconStar size={14} stroke={1.5} />}
                  loading={reviewBusy}
                  disabled={isEmailChannel}
                  onClick={confirmRequestReview}
                >
                  Оценка моей работы
                </Button>
              </Box>
            </Tooltip>
          )
        ) : null}
        {showProductReview ? (
          <>
            {hasOtherProductRequests ? (
              <Alert color="yellow" variant="light" p="xs">
                <Text size="xs">
                  Отзыв о продукте уже запрашивался в других обращениях этого клиента.
                </Text>
              </Alert>
            ) : null}
            {productReviewStatus?.requested ? (
              <Stack gap={4}>
                <Text size="xs" c="dimmed">
                  Запрос отзыва о продукте отправлен.
                  {productReviewStatus.review_received_at
                    ? ` Получен: ${formatDateTimeDot(productReviewStatus.review_received_at)}`
                    : ''}
                </Text>
                <Checkbox
                  size="xs"
                  label="Отзыв получен"
                  checked={!!productReviewStatus.review_received}
                  onChange={(e) => onToggleProductReviewReceived(e.currentTarget.checked)}
                  disabled={productReviewBusy || !productReviewStatus.id}
                />
              </Stack>
            ) : canPostProductReview ? (
              <Box style={{ width: '100%' }}>
                <Button
                  size="compact-xs"
                  variant="light"
                  fullWidth
                  loading={productReviewBusy}
                  onClick={confirmRequestProductReview}
                >
                  Запросить отзыв
                </Button>
              </Box>
            ) : (
              <Text size="xs" c="dimmed">
                Для закрытого обращения запрос отзыва о продукте недоступен.
              </Text>
            )}
          </>
        ) : null}
      </Stack>
    </Card>
  )
}
